import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Avatar, Box, Card, CardContent, Grid, Stack, Typography, Chip, Divider, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Paper, CircularProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, TextField, Alert } from '@mui/material';
import CreateSubMemberDialog from './CreateSubMemberDialog';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { removeSubMember, fetchMemberById, fetchMemberChildren, createMemberChild, updateMemberChild, deleteMemberChild } from '../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import EditMemberDialog, { DIALOG_INPUT_SX, LabeledControl } from './EditMemberDialog';
import { fetchGroupById } from '../../controllers/groupManagementController';
import EditSubMemberDialog from './EditSubMemberDialog';
import ForceChangePasswordDialog, {
  MEMBER_ACCOUNT_TYPE,
  SUB_MEMBER_ACCOUNT_TYPE,
} from './ForceChangePasswordDialog';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import StarIcon from '@mui/icons-material/Star';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import { fetchMemberAttendanceSummaryByMember } from '../../controllers/meetingsController';
import { getCurrencyDisplayLabel } from '../../utils/currencyDisplay';
import { fetchDonationCardPurchasesByMemberIdPurchased, fetchDonationCardPurchasesByMemberIdDonatedTo, fetchDonationCardPurchaseById } from '../../controllers/paymentsController';
import DonationLogoInfoSection from '../payments/components/DonationLogoInfoSection';
import { resolvePaymentsFileUrl } from '../../api/httpClientPayments';
import BackButton from '../../components/BackButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MemberAccountSection from './components/MemberAccountSection';

const getMembershipTypeLabel = (type) => {
  switch (type) {
    case 0: return 'Devamlı Üye';
    case 1: return 'Devamdan Muaf Üye';
    case 2: return 'Aidattan Muaf Üye';
    case 3: return 'Onursal Üye';
    case 4: return 'İzinli Üye';
    default: return 'Bilinmiyor';
  }
};

const getMembershipTypeColor = (type) => {
  switch (type) {
    case 0: return { bg: '#E7EDF4', color: '#0E4D94' }; // Regular - mavi
    case 1: return { bg: '#FFF8E1', color: '#FFA500' }; // ExcusedAttendance - turuncu
    case 2: return { bg: '#DFFFEA', color: '#17C653' }; // ExcusedDues - yeşil
    case 3: return { bg: '#F3E5F5', color: '#9C27B0' }; // Honorary - mor
    case 4: return { bg: '#E0F2F1', color: '#00796B' }; // Permitted - teal
    default: return { bg: '#e6e6e6', color: '#666666' };
  }
};

const formatBirthDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR');
};

const formatDateTime = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Donation Card Enums
const DonationCardPurchaseStatus = {
  Pending: 0,
  Paid: 1,
  Cancelled: 2,
  AwaitingApproval: 3,
};

const DonationCardPurchaseStatusNames = {
  0: 'Beklemede',
  1: 'Ödendi',
  2: 'İptal Edildi',
  3: 'Onay Bekliyor',
};

const PaymentMethodNames = {
  0: 'Nakit',
  1: 'Banka Havalesi/EFT',
  2: 'Kredi Kartı',
  3: 'Online Ödeme',
};

/** Üye detayı › Bağış Kartları tabloları — ödemeler › Bağış Kartları liste tablosu ile aynı dil */
const DONATION_MEMBER_TABLE_HEAD_SX = {
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
};

const DONATION_MEMBER_TABLE_PAPER_SX = {
  py: 1,
  px: 2.5,
  borderRadius: '8px',
  border: '1px solid #ececec',
  boxShadow: 'none',
};

const getDonationMemberTableRowSx = (rowIndex, totalRows) => ({
  '& .MuiTableCell-root': {
    borderBottom: rowIndex === totalRows - 1 ? 'none' : '1px dashed #e8e8e8',
    px: 1.5,
    py: 1.5,
    color: '#444444',
    fontSize: '14px',
  },
  '&:hover': {
    backgroundColor: '#fafafa',
  },
});

const getDonationStatusChipSxMemberTable = (status) => {
  const isPaid = status === DonationCardPurchaseStatus.Paid;
  const isAwait = status === DonationCardPurchaseStatus.AwaitingApproval;
  const isCancelled = status === DonationCardPurchaseStatus.Cancelled;
  return {
    height: 24,
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '12px',
    border: 'none',
    '& .MuiChip-label': { px: 1.25 },
    backgroundColor: isPaid ? '#DFFFEA' : isAwait ? '#FFF4E6' : isCancelled ? '#F5F5F5' : '#E7EDF4',
    color: isPaid ? '#17C653' : isAwait ? '#FF9800' : isCancelled ? '#666666' : '#0E4D94',
  };
};

const DONATION_DETAIL_VIEW_ICONBUTTON_SX = {
  backgroundColor: '#E7EDF4',
  borderRadius: '6px',
  width: 32,
  height: 32,
  '&:hover': { backgroundColor: '#CFDBEA' },
  '& svg': { fontSize: '16px', color: '#0E4D94' },
};

/** Makbuz satırı — etiket solda, değer sağda; mobilde dikey stack */
function DonationReceiptRow({ label, children, multiline }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: multiline ? 'column' : { xs: 'column', sm: 'row' },
        alignItems: multiline ? 'stretch' : { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: multiline ? 1 : { xs: 0.75, sm: 2 },
        py: multiline ? 2 : 1.625,
      }}
    >
      <Typography sx={{ fontSize: '13px', color: '#888888', fontWeight: 500, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box
        sx={{
          ...(multiline
            ? { width: '100%' }
            : { flex: 1, minWidth: 0, textAlign: { xs: 'left', sm: 'right' } }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function DonationDetailModalBody({ donationDetail: d }) {
  const paymentMethodLabel =
    d.paymentMethod !== undefined && d.paymentMethod !== null ? PaymentMethodNames[d.paymentMethod] || '-' : null;
  const formattedAmount =
    `${d.amount?.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) || '0,00'}${d.currency ? ` ${getCurrencyDisplayLabel(d.currency)}` : ''}`;

  return (
    <Stack divider={<Divider flexItem sx={{ borderColor: '#ececec' }} />}>
      {/* Taraflar — düz blok, kutusuz */}
      <Box sx={{ py: 2 }}>
        <Grid container spacing={{ xs: 2.5, sm: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '12px', color: '#a3a3a3', fontWeight: 500, mb: 1 }}>
              Satın alan
            </Typography>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar src={resolveGroupsAssetUrl(d.purchasedByProfilePictureUrl)} sx={{ width: 40, height: 40 }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333333', lineHeight: 1.35 }}>
                {d.purchasedByFullName || '—'}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '12px', color: '#a3a3a3', fontWeight: 500, mb: 1 }}>
              Bağış yapılan
            </Typography>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar src={resolveGroupsAssetUrl(d.donatedToProfilePictureUrl)} sx={{ width: 40, height: 40 }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333333', lineHeight: 1.35 }}>
                {d.donatedToFullName || '—'}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <DonationReceiptRow label="Tutar">
        <Typography
          sx={{
            fontSize: '22px',
            fontWeight: 700,
            color: '#0E4D94',
            lineHeight: 1.25,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formattedAmount}
        </Typography>
      </DonationReceiptRow>

      <DonationReceiptRow label="Adet">
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333333', fontVariantNumeric: 'tabular-nums' }}>
          {d.quantity ?? '—'}
        </Typography>
      </DonationReceiptRow>

      <DonationReceiptRow label="Durum">
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Chip
            label={d.statusName || DonationCardPurchaseStatusNames[d.status]}
            size="small"
            sx={getDonationStatusChipSxMemberTable(d.status)}
          />
        </Box>
      </DonationReceiptRow>

      {paymentMethodLabel ? (
        <DonationReceiptRow label="Ödeme yöntemi">
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>{paymentMethodLabel}</Typography>
        </DonationReceiptRow>
      ) : null}

      {d.referenceNumber ? (
        <DonationReceiptRow label="Referans numarası">
          <Typography
            component="span"
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#333333',
              wordBreak: 'break-all',
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            }}
          >
            {d.referenceNumber}
          </Typography>
        </DonationReceiptRow>
      ) : null}

      {d.paymentDate ? (
        <DonationReceiptRow label="Ödeme tarihi">
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333', fontVariantNumeric: 'tabular-nums' }}>
            {new Date(d.paymentDate).toLocaleString('tr-TR')}
          </Typography>
        </DonationReceiptRow>
      ) : null}

      <DonationReceiptRow label="Kayıt tarihi">
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333', fontVariantNumeric: 'tabular-nums' }}>
          {d.createdAt ? new Date(d.createdAt).toLocaleString('tr-TR') : '—'}
        </Typography>
      </DonationReceiptRow>

      {d.description ? (
        <DonationReceiptRow label="Açıklama" multiline>
          <Typography sx={{ fontSize: '14px', color: '#444444', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {d.description}
          </Typography>
        </DonationReceiptRow>
      ) : null}

      {d.paymentProofUrl ? (
        <DonationReceiptRow label="Ödeme kanıtı">
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Button
              variant="outlined"
              component="a"
              href={resolvePaymentsFileUrl(d.paymentProofUrl)}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 500,
                borderRadius: '5px',
                borderColor: '#e6e6e6',
                color: '#0E4D94',
                '&:hover': {
                  borderColor: '#0E4D94',
                  backgroundColor: '#E7EDF4',
                },
              }}
            >
              Görüntüle
            </Button>
          </Box>
        </DonationReceiptRow>
      ) : null}

      <Box sx={{ py: 2 }}>
        <DonationLogoInfoSection detail={d} variant="inline" />
      </Box>
    </Stack>
  );
}

const MemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordDialogTarget, setPasswordDialogTarget] = useState(null);
  /** Hangi karttan düzenle açıldı: her biri yalnızca ilgili alanları gösterir */
  const [editSection, setEditSection] = useState(null);
  /** Üye detayı ana sekmeleri (Kişisel → Yakınlar) */
  const [detailTab, setDetailTab] = useState(0);
  const [referenceMember, setReferenceMember] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [editSubOpen, setEditSubOpen] = useState(false);
  const [selectedSubForEdit, setSelectedSubForEdit] = useState(null);
  const [meetingAttendanceSummary, setMeetingAttendanceSummary] = useState(null);
  const [loadingMeetingAttendance, setLoadingMeetingAttendance] = useState(false);

  // Donation Cards
  const [donationTab, setDonationTab] = useState(0); // 0 = Purchased, 1 = DonatedTo
  const [purchasedDonations, setPurchasedDonations] = useState([]);
  const [donatedToDonations, setDonatedToDonations] = useState([]);
  const [purchasedPage, setPurchasedPage] = useState(1);
  const [donatedToPage, setDonatedToPage] = useState(1);
  const [purchasedTotalPages, setPurchasedTotalPages] = useState(1);
  const [donatedToTotalPages, setDonatedToTotalPages] = useState(1);
  const [loadingPurchased, setLoadingPurchased] = useState(false);
  const [loadingDonatedTo, setLoadingDonatedTo] = useState(false);
  const [donationDetailOpen, setDonationDetailOpen] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [donationDetail, setDonationDetail] = useState(null);
  const [loadingDonationDetail, setLoadingDonationDetail] = useState(false);

  const handleOpenDonationDetail = useCallback(async (donation) => {
    setSelectedDonation(donation);
    setDonationDetailOpen(true);
    setLoadingDonationDetail(true);
    try {
      const detail = await fetchDonationCardPurchaseById(donation.id);
      setDonationDetail(detail);
    } catch (e) {
      setDonationDetail(null);
    } finally {
      setLoadingDonationDetail(false);
    }
  }, []);

  const closeDonationDetail = useCallback(() => {
    setDonationDetailOpen(false);
    setSelectedDonation(null);
    setDonationDetail(null);
  }, []);

  // Children
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [childForm, setChildForm] = useState({ firstName: '', lastName: '', birthDate: '' });
  const [childSaving, setChildSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const d = await fetchMemberById(id);
      setData(d);
    })();
  }, [id]);

  useEffect(() => {
    if (!data?.referenceMemberId) { setReferenceMember(null); return; }
    let active = true;
    (async () => {
      try {
        const ref = await fetchMemberById(data.referenceMemberId);
        if (active) setReferenceMember(ref);
      } catch (e) {
        if (active) setReferenceMember(null);
      }
    })();
    return () => { active = false; };
  }, [data?.referenceMemberId]);

  useEffect(() => {
    if (!data?.groupId) { setGroupDetail(null); return; }
    let active = true;
    (async () => {
      try {
        const g = await fetchGroupById(data.groupId);
        if (active) setGroupDetail(g);
      } catch (e) {
        if (active) setGroupDetail(null);
      }
    })();
    return () => { active = false; };
  }, [data?.groupId]);

  // Fetch children when member data loaded
  useEffect(() => {
    if (!data?.id) { setChildren([]); return; }
    let active = true;
    (async () => {
      setChildrenLoading(true);
      try {
        const list = await fetchMemberChildren(data.id);
        if (!active) return;
        setChildren(list || []);
      } catch (e) {
        if (!active) return;
        setChildren([]);
      } finally {
        if (active) setChildrenLoading(false);
      }
    })();
    return () => { active = false; };
  }, [data?.id]);

  const handleChildFieldChange = (key) => (e) => {
    setChildForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSaveChild = async () => {
    if (!data?.id) return;
    setChildSaving(true);
    try {
      if (editingChild) {
        await updateMemberChild({
          id: editingChild.id,
          firstName: childForm.firstName?.trim(),
          lastName: childForm.lastName?.trim(),
          birthDate: childForm.birthDate ? new Date(childForm.birthDate).toISOString() : null,
        });
      } else {
        await createMemberChild({
          memberId: data.id,
          firstName: childForm.firstName?.trim(),
          lastName: childForm.lastName?.trim(),
          birthDate: childForm.birthDate ? new Date(childForm.birthDate).toISOString() : null,
        });
      }
      const list = await fetchMemberChildren(data.id);
      setChildren(list || []);
      setChildDialogOpen(false);
      setEditingChild(null);
      setChildForm({ firstName: '', lastName: '', birthDate: '' });
    } catch (e) {
      // optional error handling (toast vs. silent)
    } finally {
      setChildSaving(false);
    }
  };

  // Fetch weekly meeting attendance summary for this member (last 6 months)
  useEffect(() => {
    if (!data?.id) {
      setMeetingAttendanceSummary(null);
      return;
    }
    let active = true;
    setLoadingMeetingAttendance(true);
    (async () => {
      try {
        const today = new Date();
        const to = new Date(today);
        to.setDate(to.getDate() + 1); // güvenli aralık için bugünden 1 gün sonrası
        const from = new Date(today);
        from.setMonth(from.getMonth() - 6); // son 6 ay

        const summary = await fetchMemberAttendanceSummaryByMember({
          memberId: data.id,
          isSubMember: false,
          from: from.toISOString(),
          to: to.toISOString(),
        });
        if (active) setMeetingAttendanceSummary(summary);
      } catch (e) {
        if (active) setMeetingAttendanceSummary(null);
      } finally {
        if (active) setLoadingMeetingAttendance(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [data?.id]);

  // Fetch purchased donations
  useEffect(() => {
    if (!data?.id) {
      setPurchasedDonations([]);
      setPurchasedTotalPages(1);
      return;
    }
    let active = true;
    setLoadingPurchased(true);
    (async () => {
      try {
        const { data: donations, meta } = await fetchDonationCardPurchasesByMemberIdPurchased(data.id, {
          pageNumber: purchasedPage,
          pageSize: 10,
        });
        if (active) {
          setPurchasedDonations(donations || []);
          setPurchasedTotalPages(meta?.totalPages || 1);
        }
      } catch (e) {
        if (active) {
          setPurchasedDonations([]);
          setPurchasedTotalPages(1);
        }
      } finally {
        if (active) setLoadingPurchased(false);
      }
    })();
    return () => { active = false; };
  }, [data?.id, purchasedPage]);

  // Fetch donated to donations
  useEffect(() => {
    if (!data?.id) {
      setDonatedToDonations([]);
      setDonatedToTotalPages(1);
      return;
    }
    let active = true;
    setLoadingDonatedTo(true);
    (async () => {
      try {
        const { data: donations, meta } = await fetchDonationCardPurchasesByMemberIdDonatedTo(data.id, {
          pageNumber: donatedToPage,
          pageSize: 10,
        });
        if (active) {
          setDonatedToDonations(donations || []);
          setDonatedToTotalPages(meta?.totalPages || 1);
        }
      } catch (e) {
        if (active) {
          setDonatedToDonations([]);
          setDonatedToTotalPages(1);
        }
      } finally {
        if (active) setLoadingDonatedTo(false);
      }
    })();
    return () => { active = false; };
  }, [data?.id, donatedToPage]);

  if (!data) return null;

  return (
    <Box>
      <BackButton />
      <Card sx={{ mb: 3, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ position: 'relative', width: 180, height: 180 }}>
              <Avatar
                src={resolveGroupsAssetUrl(data.profilePictureUrl)}
                sx={{ width: 180, height: 180, boxShadow: 4, cursor: 'pointer' }}
                onClick={() => { setPreviewSrc(resolveGroupsAssetUrl(data.profilePictureUrl)); setPreviewOpen(true); }}
              />
              <IconButton
                color="primary"
                component="label"
                size="small"
                sx={{ 
                  position: 'absolute', 
                  bottom: -12, 
                  left: '50%', 
                  transform: 'translateX(-50%)', 
                  bgcolor: 'background.paper', 
                  boxShadow: 2, 
                  width: 32,
                  height: 32,
                  borderRadius: '6px',
                  '&:hover': { bgcolor: 'background.paper' } 
                }}
                aria-label="Fotoğrafı değiştir"
              >
                <PhotoCameraIcon fontSize="small" />
                <input type="file" accept="image/*" hidden onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const urlPath = await (await import('../../controllers/memberManagementController')).updateMemberProfilePicture({ MemberId: data.id, ProfilePicture: file });
                    // Update only picture url in state
                    setData((prev) => ({ ...prev, profilePictureUrl: urlPath }));
                  } catch (err) {}
                }} />
              </IconButton>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>{data.fullName}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                {data.membershipType !== undefined && (
                  <Chip
                    label={getMembershipTypeLabel(data.membershipType ?? 0)}
                    size="small"
                    sx={{
                      backgroundColor: getMembershipTypeColor(data.membershipType ?? 0).bg,
                      color: getMembershipTypeColor(data.membershipType ?? 0).color,
                      height: 24,
                      fontSize: '11px',
                      fontWeight: 500,
                    }}
                  />
                )}
                {data.jobTitle && (
                  <Chip 
                    label={data.jobTitle} 
                    size="small" 
                    sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
                  />
                )}
                {data.company && (
                  <Chip 
                    label={data.company} 
                    size="small" 
                    sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
                  />
                )}
                {data.sector && (
                  <Chip 
                    label={data.sector} 
                    size="small" 
                    sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
                  />
                )}
              </Stack>
            </Box>
            <Box sx={{ flexGrow: 1, alignSelf: 'stretch', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', pr: 1 }}>
              <Typography sx={{ fontSize: '12px', color: '#8a8a8a' }}>
                Son Giriş: {formatDateTime(data.lastLogin)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
        <Tabs
          value={detailTab}
          onChange={(e, v) => setDetailTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            pt: 1,
            borderBottom: '1px solid #e7e7e7',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              minHeight: 48,
              color: '#a3a3a3',
              '&.Mui-selected': { color: '#0E4D94' },
            },
            '& .MuiTabs-indicator': { backgroundColor: '#0E4D94' },
          }}
        >
          <Tab label="Kişisel Bilgiler" />
          <Tab label="Muhasebe" />
          <Tab label="Sosyal Medya" />
          <Tab label="Eğitim Bilgileri" />
          <Tab label="Çocuklar" />
          <Tab label="Kulüp Bilgileri" />
          <Tab label="Haftalık Toplantı Katılımı" />
          <Tab label="Bağış Kartları" />
          <Tab label="Yakınlar" />
        </Tabs>
        <CardContent sx={{ pt: 3 }}>
        {detailTab === 0 && (
        <Box>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setPasswordDialogTarget({
                    accountId: data.id,
                    memberName: data.fullName,
                    accountType: MEMBER_ACCOUNT_TYPE,
                  });
                  setPasswordDialogOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  boxShadow: 'none',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    backgroundColor: '#E7EDF4',
                  },
                }}
              >
                Şifre Değiştir
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setEditSection('personal')}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  boxShadow: 'none',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    backgroundColor: '#E7EDF4',
                  },
                }}
              >
                Düzenle
              </Button>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Ad</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                  {data.firstName || data.fullName?.split(' ')[0] || '-'}
                </Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Soyad</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                  {data.lastName || data.fullName?.split(' ').slice(1).join(' ') || '-'}
                </Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>E-posta</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.email || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Telefon</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.phoneNumber || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>TC Kimlik No</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.tcKimlikNo || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Doğum Tarihi</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                  {data.birthDate ? new Date(data.birthDate).toLocaleDateString('tr-TR') : '-'}
                </Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Ünvan</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.jobTitle || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Şirket</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.company || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Sektör</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.sector || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Logo Cari Kod</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.logoCariCode || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Ev Adresi</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.address || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>İş Adresi</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.workAddress || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Web Sitesi</Typography>
                {data.website ? (
                  <Typography
                    component="a"
                    href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500, textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {data.website}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>-</Typography>
                )}
              </Box>
              <Box sx={{ flex: '0 0 calc(100% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Hobiler</Typography>
                {data.hobbies?.trim() ? (
                  <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                    {data.hobbies.split(',').map((s) => s.trim()).filter(Boolean).map((h) => (
                      <Chip key={h} label={h} size="small" sx={{ fontSize: '12px' }} />
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>-</Typography>
                )}
              </Box>
            </Box>
        </Box>
        )}
        {detailTab === 1 && data?.id && (
          <MemberAccountSection memberId={data.id} active={detailTab === 1} />
        )}
        {detailTab === 2 && (
        <Box>
        {/* Sosyal Medya */}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setEditSection('social')}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  boxShadow: 'none',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    backgroundColor: '#E7EDF4',
                  }
                }}
              >
                Düzenle
              </Button>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Facebook</Typography>
                {data.facebook ? (
                  <Typography
                    component="a"
                    href={data.facebook.startsWith('http') ? data.facebook : `https://${data.facebook}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500, textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {data.facebook}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>-</Typography>
                )}
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Twitter</Typography>
                {data.twitter ? (
                  <Typography
                    component="a"
                    href={data.twitter.startsWith('http') ? data.twitter : `https://${data.twitter}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500, textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {data.twitter}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>-</Typography>
                )}
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>LinkedIn</Typography>
                {data.linkedIn ? (
                  <Typography
                    component="a"
                    href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500, textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {data.linkedIn}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>-</Typography>
                )}
              </Box>
            </Box>
        </Box>
        )}
        {detailTab === 3 && (
        <Box>
        {/* Eğitim Bilgileri */}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setEditSection('education')}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  boxShadow: 'none',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    backgroundColor: '#E7EDF4',
                  }
                }}
              >
                Düzenle
              </Button>
              </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Üniversite</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.university || '-'}</Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Lise</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{data.highSchool || '-'}</Typography>
              </Box>
            </Box>
        </Box>
        )}
        {detailTab === 4 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingChild(null);
                setChildForm({ firstName: '', lastName: '', birthDate: '' });
                setChildDialogOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                borderRadius: '5px',
                boxShadow: 'none',
                borderColor: '#e6e6e6',
                color: '#444444',
                '&:hover': {
                  borderColor: '#0E4D94',
                  backgroundColor: '#E7EDF4',
                },
              }}
            >
              Çocuk Ekle
            </Button>
          </Stack>

          {childrenLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : children.length === 0 ? (
            <Typography sx={{ fontSize: '14px', color: '#a3a3a3', py: 1 }}>
              Kayıtlı çocuk bulunmuyor.
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem sx={{ borderColor: '#e6e6e6' }} />} spacing={2.5}>
              {children.map((c) => {
                const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim() || '-';
                const birthLabel = c.birthDate
                  ? new Date(c.birthDate).toLocaleDateString('tr-TR')
                  : '-';
                const outlinedActionSx = {
                  textTransform: 'none',
                  fontSize: 12,
                  borderRadius: '5px',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    backgroundColor: '#E7EDF4',
                  },
                };
                return (
                  <Box key={c.id}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                      justifyContent="space-between"
                    >
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, flex: 1, minWidth: 0 }}>
                        <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '180px' }}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>
                            Ad Soyad
                          </Typography>
                          <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                            {fullName}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '180px' }}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>
                            Doğum Tarihi
                          </Typography>
                          <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                            {birthLabel}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={1} flexShrink={0} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditOutlinedIcon sx={{ width: 16, height: 16 }} />}
                          onClick={() => {
                            setEditingChild(c);
                            setChildForm({
                              firstName: c.firstName || '',
                              lastName: c.lastName || '',
                              birthDate: c.birthDate ? c.birthDate.substring(0, 10) : '',
                            });
                            setChildDialogOpen(true);
                          }}
                          sx={outlinedActionSx}
                        >
                          Düzenle
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={async () => {
                            try {
                              await deleteMemberChild(c.id);
                              const list = await fetchMemberChildren(data.id);
                              setChildren(list || []);
                            } catch (e) {
                              // hata toast'ı eklenebilir
                            }
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            borderRadius: '5px',
                          }}
                        >
                          Sil
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
        )}
        {detailTab === 5 && (
        <Box>
        {/* Kulüp Bilgileri */}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setEditSection('club')}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  boxShadow: 'none',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    backgroundColor: '#E7EDF4',
                  }
                }}
              >
                Düzenle
              </Button>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Grup</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                  {groupDetail?.name || '-'}
                </Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Kulübe Giriş Tarihi</Typography>
                <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                  {data.clubJoinDate ? new Date(data.clubJoinDate).toLocaleDateString('tr-TR') : '-'}
                </Typography>
              </Box>
              <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Üyelik Tipi</Typography>
                <Chip
                  label={getMembershipTypeLabel(data.membershipType ?? 0)}
                  size="small"
                  sx={{
                    backgroundColor: getMembershipTypeColor(data.membershipType ?? 0).bg,
                    color: getMembershipTypeColor(data.membershipType ?? 0).color,
                    height: 24,
                    fontSize: '11px',
                    fontWeight: 500,
                    mt: 0.5,
                  }}
                />
              </Box>
              {referenceMember && (
                <Box sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: '200px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Referans Üye</Typography>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ cursor: 'pointer', mt: 0.5 }}
                    onClick={() => navigate(`/members/${referenceMember.id}`)}
                  >
                    <Avatar src={resolveGroupsAssetUrl(referenceMember.profilePictureUrl)} sx={{ width: 40, height: 40 }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', '&:hover': { textDecoration: 'underline' } }}>
                      {referenceMember.fullName}
                    </Typography>
              </Stack>
                </Box>
              )}
            </Box>
            {groupDetail && groupDetail.members && groupDetail.members.length > 0 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e6e6e6' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#444444', mb: 1.5 }}>Grup Üyeleri</Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2,
                  }}
                >
                    {groupDetail.members?.map((m) => {
                      const isLeader = groupDetail.leaderId === m.id;
                    const hasSubMembers = m.subMembers?.length > 0;
                      return (
                      <Card
                        key={m.id}
                        sx={{
                          p: 2,
                          border: '1px solid #e6e6e6',
                          boxShadow: 'none',
                          borderRadius: '8px',
                          position: 'relative',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                          },
                        }}
                      >
                        {hasSubMembers ? (
                          // Yakını olan kişiler için özel tasarım
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            {/* Sol taraf - Görseller */}
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Avatar
                                src={resolveGroupsAssetUrl(m.profilePicture)}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  border: '2px solid #e6e6e6',
                                  cursor: 'pointer',
                                }}
                                onClick={() => navigate(`/members/${m.id}`)}
                              >
                                {!m.profilePicture && (
                                  <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                                    {m.fullName?.charAt(0) || '?'}
                                  </Typography>
                                )}
                              </Avatar>
                              {m.subMembers.map((s) => (
                                <Avatar
                                  key={s.id}
                                  src={resolveGroupsAssetUrl(s.profilePicture)}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    border: '2px solid #e6e6e6',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => navigate(`/members/${s.id}`)}
                                >
                                  {!s.profilePicture && (
                                    <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                                      {s.fullName?.charAt(0) || '?'}
                                    </Typography>
                                  )}
                                </Avatar>
                              ))}
                            </Stack>
                            {/* Sağ taraf - İsimler */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack spacing={0.5}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      color: '#444444',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                    onClick={() => navigate(`/members/${m.id}`)}
                                  >
                                    {m.fullName}
                                  </Typography>
                            {isLeader && (
                              <Tooltip title="Grup Lideri">
                                      <StarIcon sx={{ color: '#FFA500', fontSize: 16, flexShrink: 0 }} />
                              </Tooltip>
                            )}
                                </Stack>
                                {m.subMembers.map((s) => (
                                  <Typography
                                    key={s.id}
                                    variant="body2"
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      color: '#444444',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                    onClick={() => navigate(`/members/${s.id}`)}
                                  >
                                    {s.fullName}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          </Stack>
                        ) : (
                          // Yakını olmayan kişiler için normal tasarım
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              src={resolveGroupsAssetUrl(m.profilePicture)}
                              sx={{
                                width: 48,
                                height: 48,
                                border: '2px solid #e6e6e6',
                                cursor: 'pointer',
                              }}
                              onClick={() => navigate(`/members/${m.id}`)}
                            >
                              {!m.profilePicture && (
                                <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                                  {m.fullName?.charAt(0) || '?'}
                                </Typography>
                              )}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#444444',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}
                                  onClick={() => navigate(`/members/${m.id}`)}
                                >
                                  {m.fullName}
                                </Typography>
                                {isLeader && (
                                  <Tooltip title="Grup Lideri">
                                    <StarIcon sx={{ color: '#FFA500', fontSize: 16, flexShrink: 0 }} />
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                        )}
                      </Card>
                      );
                    })}
                </Box>
                </Box>
              )}
        </Box>
        )}
        {detailTab === 6 && (
        <Box>
        {/* Haftalık Toplantı Katılımı (üyeye özel) */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <EventIcon fontSize="small" sx={{ color: '#0E4D94' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#888' }}>
                Son 6 ay
              </Typography>
            </Stack>
            {loadingMeetingAttendance ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : meetingAttendanceSummary ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
                  gap: 1.5,
                }}
              >
                <Card
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderTop: '3px solid',
                    borderColor: '#0E4D94',
                    border: '1px solid #e7e7e7',
                    boxShadow: 'none',
                    borderRadius: '8px',
                  }}
                >
                  <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#0E4D94' }}>
                    {meetingAttendanceSummary.totalMeetings ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#a3a3a3', fontWeight: 500, mt: 0.5 }}>
                    Toplam Toplantı
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderTop: '3px solid',
                    borderColor: '#17C653',
                    border: '1px solid #e7e7e7',
                    boxShadow: 'none',
                    borderRadius: '8px',
                  }}
                >
                  <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#17C653' }}>
                    {meetingAttendanceSummary.attendedMeetings ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#a3a3a3', fontWeight: 500, mt: 0.5 }}>
                    Katıldığı
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderTop: '3px solid',
                    borderColor: '#2980B9',
                    border: '1px solid #e7e7e7',
                    boxShadow: 'none',
                    borderRadius: '8px',
                  }}
                >
                  <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#2980B9' }}>
                    {meetingAttendanceSummary.excusedMeetings ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#a3a3a3', fontWeight: 500, mt: 0.5 }}>
                    İzinli
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderTop: '3px solid',
                    borderColor: '#F8285A',
                    border: '1px solid #e7e7e7',
                    boxShadow: 'none',
                    borderRadius: '8px',
                  }}
                >
                  <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#F8285A' }}>
                    {meetingAttendanceSummary.absentMeetings ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#a3a3a3', fontWeight: 500, mt: 0.5 }}>
                    Gelmediği
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderLeft: '3px solid',
                    borderColor: '#0E4D94',
                    border: '1px solid #e7e7e7',
                    boxShadow: 'none',
                    borderRadius: '8px',
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                    Katılım Oranı
                  </Typography>
                  <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600, mt: 0.5 }}>
                    {Number(meetingAttendanceSummary.attendanceRate ?? 0).toFixed(1)}%
                  </Typography>
                </Card>
              </Box>
            ) : (
              <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                Haftalık toplantı katılım verisi bulunamadı.
              </Typography>
            )}
        </Box>
        )}
        {detailTab === 7 && (
        <Box>
        {/* Bağış Kartları */}
            <Tabs
              value={donationTab}
              onChange={(e, v) => {
                setDonationTab(v);
                setPurchasedPage(1);
                setDonatedToPage(1);
              }}
              sx={{
                mb: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#a3a3a3',
                  '&.Mui-selected': {
                    color: '#0E4D94',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#0E4D94',
                },
              }}
            >
              <Tab label="Satın Aldıkları" />
              <Tab label="Kendisine Yapılan Bağışlar" />
            </Tabs>

            {/* Tab 0: Satın Aldıkları */}
            {donationTab === 0 && (
              <>
                {loadingPurchased ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : purchasedDonations.length === 0 ? (
                  <Typography sx={{ fontSize: '14px', color: '#a3a3a3', textAlign: 'center', py: 3 }}>
                    Satın alınan bağış kartı bulunamadı.
                  </Typography>
                ) : (
                  <>
                    <TableContainer component={Paper} elevation={0} sx={DONATION_MEMBER_TABLE_PAPER_SX}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Bağış Yapılan</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Adet</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Tutar</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Durum</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Tarih</TableCell>
                            <TableCell align="right" sx={DONATION_MEMBER_TABLE_HEAD_SX}>
                              İşlemler
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {purchasedDonations.map((donation, index) => (
                            <TableRow
                              key={donation.id}
                              hover
                              sx={getDonationMemberTableRowSx(index, purchasedDonations.length)}
                            >
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Avatar
                                    src={resolveGroupsAssetUrl(donation.donatedToProfilePictureUrl)}
                                    sx={{ width: 32, height: 32 }}
                                  />
                                  <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                                    {donation.donatedToFullName}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>{donation.quantity}</TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {donation.amount?.toLocaleString('tr-TR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }) || '0.00'}{' '}
                                {getCurrencyDisplayLabel(donation.currency)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    donation.statusName || DonationCardPurchaseStatusNames[donation.status]
                                  }
                                  size="small"
                                  sx={getDonationStatusChipSxMemberTable(donation.status)}
                                />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {donation.createdAt
                                  ? new Date(donation.createdAt).toLocaleDateString('tr-TR')
                                  : '-'}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Detay">
                                  <IconButton
                                    size="small"
                                    sx={DONATION_DETAIL_VIEW_ICONBUTTON_SX}
                                    onClick={() => handleOpenDonationDetail(donation)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {purchasedTotalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Pagination
                          count={purchasedTotalPages}
                          page={purchasedPage}
                          onChange={(e, v) => setPurchasedPage(v)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    )}
                  </>
                )}
              </>
            )}

            {/* Tab 1: Kendisine Yapılan Bağışlar */}
            {donationTab === 1 && (
              <>
                {loadingDonatedTo ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : donatedToDonations.length === 0 ? (
                  <Typography sx={{ fontSize: '14px', color: '#a3a3a3', textAlign: 'center', py: 3 }}>
                    Kendisine yapılan bağış bulunamadı.
                  </Typography>
                ) : (
                  <>
                    <TableContainer component={Paper} elevation={0} sx={DONATION_MEMBER_TABLE_PAPER_SX}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Bağış Yapan</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Adet</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Tutar</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Durum</TableCell>
                            <TableCell sx={DONATION_MEMBER_TABLE_HEAD_SX}>Tarih</TableCell>
                            <TableCell align="right" sx={DONATION_MEMBER_TABLE_HEAD_SX}>
                              İşlemler
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {donatedToDonations.map((donation, index) => (
                            <TableRow
                              key={donation.id}
                              hover
                              sx={getDonationMemberTableRowSx(index, donatedToDonations.length)}
                            >
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Avatar
                                    src={resolveGroupsAssetUrl(donation.purchasedByProfilePictureUrl)}
                                    sx={{ width: 32, height: 32 }}
                                  />
                                  <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                                    {donation.purchasedByFullName}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>{donation.quantity}</TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {donation.amount?.toLocaleString('tr-TR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }) || '0.00'}{' '}
                                {getCurrencyDisplayLabel(donation.currency)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    donation.statusName || DonationCardPurchaseStatusNames[donation.status]
                                  }
                                  size="small"
                                  sx={getDonationStatusChipSxMemberTable(donation.status)}
                                />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {donation.createdAt
                                  ? new Date(donation.createdAt).toLocaleDateString('tr-TR')
                                  : '-'}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Detay">
                                  <IconButton
                                    size="small"
                                    sx={DONATION_DETAIL_VIEW_ICONBUTTON_SX}
                                    onClick={() => handleOpenDonationDetail(donation)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {donatedToTotalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Pagination
                          count={donatedToTotalPages}
                          page={donatedToPage}
                          onChange={(e, v) => setDonatedToPage(v)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
        </Box>
        )}
        {detailTab === 8 && (
        <Box>
      {data.subMembers?.length > 0 ? (
        <Box>
          <Grid container spacing={2}>
            {data.subMembers.map((s) => (
              <Grid key={s.id} item xs={12} md={6} lg={4}>
                <Card
                  sx={{
                    border: '1px solid #e6e6e6',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': {
                      borderColor: '#0E4D94',
                      boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                          <Avatar
                            src={resolveGroupsAssetUrl(s.profilePictureUrl)}
                            sx={{
                              width: 56,
                              height: 56,
                              cursor: 'pointer',
                              border: '1px solid #e6e6e6',
                            }}
                            onClick={() => {
                              setPreviewSrc(resolveGroupsAssetUrl(s.profilePictureUrl));
                              setPreviewOpen(true);
                            }}
                          />
                          <IconButton
                            component="label"
                            size="small"
                            sx={{
                              position: 'absolute',
                              bottom: -6,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 30,
                              height: 30,
                              borderRadius: '6px',
                              border: '1px solid #e6e6e6',
                              bgcolor: '#fff',
                              boxShadow: 'none',
                              color: '#0E4D94',
                              '&:hover': {
                                borderColor: '#0E4D94',
                                bgcolor: '#E7EDF4',
                              },
                            }}
                            aria-label="Yakın fotoğrafını değiştir"
                          >
                            <PhotoCameraIcon sx={{ fontSize: '16px' }} />
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const urlPath = await (
                                    await import('../../controllers/memberManagementController')
                                  ).updateSubMemberProfilePicture({ SubMemberId: s.id, ProfilePicture: file });
                                  setData((prev) => ({
                                    ...prev,
                                    subMembers:
                                      prev.subMembers?.map((x) =>
                                        x.id === s.id ? { ...x, profilePictureUrl: urlPath } : x,
                                      ) || prev.subMembers,
                                  }));
                                } catch (err) {}
                              }}
                            />
                          </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444', lineHeight: 1.3 }}>
                            {s.fullName}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mt: 0.35 }}>
                            {s.phoneNumber || '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#b0b0b0', mt: 0.75 }}>
                            Son giriş: {formatDateTime(s.lastLogin)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexShrink={0}
                        flexWrap="wrap"
                        sx={{
                          pt: { xs: 1.25, sm: 0 },
                          ml: { xs: 0, sm: 'auto' },
                          justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setPasswordDialogTarget({
                              accountId: s.id,
                              memberName: s.fullName,
                              accountType: SUB_MEMBER_ACCOUNT_TYPE,
                            });
                            setPasswordDialogOpen(true);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            borderRadius: '5px',
                            borderColor: '#e6e6e6',
                            color: '#444444',
                            flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' },
                            minWidth: { xs: 0, sm: 'unset' },
                            '&:hover': {
                              borderColor: '#0E4D94',
                              backgroundColor: '#E7EDF4',
                            },
                          }}
                        >
                          Şifre Değiştir
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditOutlinedIcon sx={{ width: 16, height: 16 }} />}
                          onClick={() => {
                            setSelectedSubForEdit(s);
                            setEditSubOpen(true);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            borderRadius: '5px',
                            borderColor: '#e6e6e6',
                            color: '#444444',
                            flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' },
                            minWidth: { xs: 0, sm: 'unset' },
                            '&:hover': {
                              borderColor: '#0E4D94',
                              backgroundColor: '#E7EDF4',
                            },
                          }}
                        >
                          Düzenle
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setSelectedSub(s);
                            setConfirmOpen(true);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            borderRadius: '5px',
                            flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' },
                            minWidth: { xs: 0, sm: 'unset' },
                          }}
                        >
                          Kaldır
                        </Button>
                      </Stack>
                    </Stack>
                    <Divider sx={{ borderColor: '#e6e6e6', my: 2 }} />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 2,
                        columnGap: 3,
                        rowGap: 1.75,
                      }}
                    >
                      <Box sx={{ gridColumn: { sm: 'span 2' }, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>E-posta</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500, wordBreak: 'break-word' }}>
                          {s.email || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Ünvan</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{s.jobTitle || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Şirket</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{s.company || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Sektör</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{s.sector || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Üniversite</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{s.university || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Lise</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{s.highSchool || '-'}</Typography>
                      </Box>
                      <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' }, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>TC Kimlik No</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                          {s.tcKimlikNo || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Doğum tarihi</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{formatBirthDate(s.birthDate)}</Typography>
                      </Box>
                      <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' }, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Ev Adresi</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                          {s.address || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box>
          <Button 
            variant="contained" 
            onClick={() => setSubOpen(true)}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              borderRadius: '5px',
              boxShadow: 'none',
              backgroundColor: '#0E4D94',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#0a3d7a',
              }
            }}
          >
            Yakın Ekle
          </Button>
        </Box>
      )}
        </Box>
        )}
        </CardContent>
      </Card>

      <CreateSubMemberDialog
        open={subOpen}
        onClose={() => setSubOpen(false)}
        memberId={data.id}
        onCreated={(created) => {
          setData(created);
          setSubOpen(false);
        }}
      />

      <EditMemberDialog
        open={editSection != null}
        section={editSection}
        onClose={() => setEditSection(null)}
        member={data}
        onUpdated={(updated) => {
          setData(updated);
          setEditSection(null);
        }}
      />

      <ForceChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => {
          setPasswordDialogOpen(false);
          setPasswordDialogTarget(null);
        }}
        accountId={passwordDialogTarget?.accountId ?? data?.id}
        memberName={passwordDialogTarget?.memberName ?? data?.fullName}
        accountType={passwordDialogTarget?.accountType ?? MEMBER_ACCOUNT_TYPE}
      />

      {/* Çocuk ekle / düzenle — EditMemberDialog ile aynı modal kalıbı */}
      <Dialog
        open={childDialogOpen}
        onClose={(event, reason) => {
          if (childSaving) return;
          setChildDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          {editingChild ? 'Çocuğu düzenle' : 'Çocuk ekle'}
          <IconButton
            type="button"
            onClick={() => {
              if (childSaving) return;
              setChildDialogOpen(false);
            }}
            size="small"
            disabled={childSaving}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveChild();
          }}
        >
          <DialogContent sx={{ py: 2, px: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <LabeledControl label="Ad">
                    <TextField
                      value={childForm.firstName}
                      onChange={handleChildFieldChange('firstName')}
                      required
                      fullWidth
                      disabled={childSaving}
                      placeholder=""
                      sx={DIALOG_INPUT_SX}
                    />
                  </LabeledControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <LabeledControl label="Soyad">
                    <TextField
                      value={childForm.lastName}
                      onChange={handleChildFieldChange('lastName')}
                      required
                      fullWidth
                      disabled={childSaving}
                      placeholder=""
                      sx={DIALOG_INPUT_SX}
                    />
                  </LabeledControl>
                </Box>
              </Stack>
              <Box sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}>
                <LabeledControl label="Doğum tarihi">
                  <TextField
                    type="date"
                    value={childForm.birthDate}
                    onChange={handleChildFieldChange('birthDate')}
                    fullWidth
                    disabled={childSaving}
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ py: 2, px: 3 }}>
            <Button
              type="button"
              onClick={() => {
                if (childSaving) return;
                setChildDialogOpen(false);
              }}
              disabled={childSaving}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                color: '#666666',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={childSaving}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                backgroundColor: '#0E4D94',
                borderRadius: '5px',
                px: 2,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#0a3d7a',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: '#e6e6e6',
                  color: '#a3a3a3',
                },
              }}
            >
              {childSaving ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ borderBottom: '1px solid #e6e6e6', py: 2, px: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>Yakını Kaldır</Typography>
            <IconButton
              onClick={() => setConfirmOpen(false)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '6px',
                color: '#a3a3a3',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ py: 2.5, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            {selectedSub ? `${selectedSub.fullName} kaydını kaldırmak istediğinize emin misiniz?` : 'Seçili yakını kaldırmak istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button 
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              }
            }}
          >
            İptal
          </Button>
          <Button 
            variant="contained" 
            onClick={async () => {
            if (!selectedSub) return;
            try {
              const updated = await removeSubMember(selectedSub.id);
              setData(updated);
            } catch (e) {}
            finally {
              setSelectedSub(null);
              setConfirmOpen(false);
            }
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              px: 2,
              backgroundColor: '#F8285A',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#E01E4A',
              }
            }}
          >
            Kaldır
          </Button>
        </DialogActions>
      </Dialog>

      <EditSubMemberDialog
        open={editSubOpen}
        onClose={() => setEditSubOpen(false)}
        subMember={selectedSubForEdit}
        onUpdated={(updated) => {
          // API returns the parent member updated (with subMembers). Reflect it.
          setData(updated);
          setEditSubOpen(false);
          setSelectedSubForEdit(null);
        }}
      />

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md">
        <DialogContent sx={{ p: 0 }}>
          {previewSrc && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'black' }}>
              <Box component="img" src={previewSrc} alt="Profil" sx={{ maxWidth: '90vw', maxHeight: '80vh' }} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Bağış detayı — sade makbuz düzeni (çizgiler, kutusuz) */}
      <Dialog
        open={donationDetailOpen}
        onClose={(event, reason) => {
          if (loadingDonationDetail) return;
          closeDonationDetail();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '10px',
            border: '1px solid #e6e8eb',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.075)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '17px',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderTop: '3px solid #0E4D94',
            borderBottom: '1px solid #ececec',
            color: '#2c2c2c',
          }}
        >
          Bağış detayı
          <IconButton
            type="button"
            onClick={closeDonationDetail}
            disabled={loadingDonationDetail}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3, bgcolor: '#ffffff' }}>
          {loadingDonationDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={28} sx={{ color: '#0E4D94' }} />
            </Box>
          ) : donationDetail ? (
            <DonationDetailModalBody donationDetail={donationDetail} />
          ) : (
            <Typography sx={{ fontSize: '14px', color: '#a3a3a3', py: 2, textAlign: 'center' }}>
              Detay yüklenemedi.
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            py: 2,
            px: 3,
            borderTop: '1px solid #ececec',
            bgcolor: '#ffffff',
          }}
        >
          <Button
            type="button"
            disabled={loadingDonationDetail}
            onClick={closeDonationDetail}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberDetail;


